import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import theme from '../../../UI/theme';
import Modal from '../../UiComponents/Modal';
import InputField from '../../UiComponents/InputField';
import Button from '../../UiComponents/Button';
import { ModalContainer, ButtonRowContainer, ModalText } from '../../panel/ModalStyles';
import CustomSqlNotificationEditor, { normalizeNotificationChannel } from './CustomSqlNotificationEditor';

const FieldGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const FieldLabel = styled.label`
    font-size: ${theme.fontSize.small};
    color: ${theme.colors.textGray};
`;

const StyledTextarea = styled(InputField).attrs({ as: 'textarea' })`
    min-height: 140px;
    resize: vertical;
`;

const ErrorMessage = styled.p`
    margin: 0;
    font-size: ${theme.fontSize.small};
    color: ${theme.colors.raspberryRed};
`;

const SectionHeading = styled.h3`
    border-top: solid 2px ${theme.colors.grayBorder};
    padding-top: 24px;
    margin: 24px 0 0;
`;

const TestTableWrapper = styled.div`
    width: 100%;
    overflow-x: auto;
`;

const TestTable = styled.table`
    width: 100%;
    min-width: 100%;
    border-collapse: collapse;
    font-size: ${theme.fontSize.tiny};
`;

const TestTableHeadCell = styled.th`
    position: sticky;
    top: 0;
    padding: 10px 12px;
    text-align: left;
    background: ${theme.colors.darkSpace70};
    color: ${theme.colors.whiteSpace};
    border-bottom: 1px solid ${theme.colors.grayBorder};
    white-space: nowrap;
`;

const TestTableRow = styled.tr`
    &:nth-child(even) td:not(:first-child) {
        background: ${theme.colors.darkSpace70};
    }

    &:nth-child(even) td:first-child {
        background: ${theme.colors.darkSpace60};
    }
`;

const TestTableCell = styled.td`
    padding: 8px 12px;
    border-bottom: 1px solid ${theme.colors.grayBorder};
    background: ${({ isFirstColumn }) =>
        isFirstColumn ? theme.colors.darkSpace70 : theme.colors.darkSpace80};
    color: ${theme.colors.whiteSpace};
    white-space: nowrap;
`;

const stripSqlComments = (sql) =>
    sql
        .replace(/--.*$/gm, '')
        .replace(/\/\*[\s\S]*?\*\//g, '');

const verifySqlSyntax = (sql) => {
    const trimmed = (sql || '').trim();

    if (!trimmed) {
        return { valid: false, message: 'SQL cannot be empty.' };
    }

    const unescapedSingleQuotes = (trimmed.match(/'/g) || []).length % 2 !== 0;
    if (unescapedSingleQuotes) {
        return { valid: false, message: 'Unmatched single quote detected.' };
    }

    const unescapedDoubleQuotes = (trimmed.match(/"/g) || []).length % 2 !== 0;
    if (unescapedDoubleQuotes) {
        return { valid: false, message: 'Unmatched double quote detected.' };
    }

    let balance = 0;
    for (const char of trimmed) {
        if (char === '(') {
            balance += 1;
        } else if (char === ')') {
            balance -= 1;
            if (balance < 0) {
                return { valid: false, message: 'Unmatched closing parenthesis.' };
            }
        }
    }

    if (balance !== 0) {
        return { valid: false, message: 'Unmatched opening parenthesis.' };
    }

    const stripped = stripSqlComments(trimmed);
    const firstTokenMatch = stripped.match(/^[\s(]*([a-zA-Z]+)/);
    const firstToken = firstTokenMatch ? firstTokenMatch[1].toLowerCase() : '';
    const allowedStarters = ['select', 'with'];
    if (!allowedStarters.includes(firstToken)) {
        return { valid: false, message: 'SQL must start with SELECT or WITH for read-only access.' };
    }

    const sanitized = stripped
        .replace(/'([^']|'')*'/g, '')
        .replace(/"([^"]|"")*"/g, '');
    const upperSql = sanitized.toUpperCase();
    const forbiddenKeywords = ['INSERT', 'UPDATE', 'DELETE', 'DROP', 'ALTER', 'TRUNCATE', 'MERGE', 'CALL', 'GRANT', 'REVOKE'];

    if (forbiddenKeywords.some((keyword) => new RegExp(`\\b${keyword}\\b`).test(upperSql))) {
        return { valid: false, message: 'Only read-only queries are allowed.' };
    }

    const semicolonMatches = stripped.match(/;/g) || [];
    if (semicolonMatches.length > 1) {
        return { valid: false, message: 'Only a single SQL statement is allowed.' };
    }

    if (semicolonMatches.length === 1) {
        const lastSemicolonIndex = stripped.lastIndexOf(';');
        const trailingContent = stripped.slice(lastSemicolonIndex + 1).trim();
        if (trailingContent.length > 0) {
            return { valid: false, message: 'Only a single SQL statement is allowed.' };
        }
    }

    const normalized = trimmed.endsWith(';') ? trimmed : `${trimmed};`;

    return { valid: true, normalizedSql: normalized };
};

const defaultFormatCellValue = (value) => {
    if (value === null || value === undefined) {
        return '';
    }

    if (typeof value === 'object') {
        try {
            return JSON.stringify(value);
        } catch (error) {
            return '[object]';
        }
    }

    return String(value);
};

const CustomSqlTableModal = ({
    show,
    tableId,
    initialValues,
    onClose,
    onSave,
    onDelete,
    onTest,
    databaseStructure = [],
    formatCellValue = defaultFormatCellValue,
}) => {
    const initialName = initialValues?.name || 'Table Name';
    const initialDescription = initialValues?.description || 'Description missing';
    const initialChatGptPrompt = initialValues?.cgptPrompt || '';
    const initialSql = (initialValues?.sqlQuery || '').trim();
    const initialColumnOrder = useMemo(
        () => (Array.isArray(initialValues?.columnOrder) ? initialValues.columnOrder : []),
        [initialValues?.columnOrder]
    );
    const initialTableNumber = initialValues?.tableNumber ?? '';
    const initialTableGroup = initialValues?.tableGroup ?? '';
    const initialNotifyIf = useMemo(() => {
        if (!Array.isArray(initialValues?.notifyIf)) {
            return [];
        }

        return initialValues.notifyIf
            .map((group) => (Array.isArray(group) ? group.filter(Boolean) : []))
            .filter((group) => group.length > 0);
    }, [initialValues?.notifyIf]);

    const [editedName, setEditedName] = useState(initialName);
    const [editedDescription, setEditedDescription] = useState(initialDescription);
    const [editedChatGptPrompt, setEditedChatGptPrompt] = useState(initialChatGptPrompt);
    const [editedSql, setEditedSql] = useState(initialSql);
    const [editedColumnOrder, setEditedColumnOrder] = useState(initialColumnOrder);
    const [editedColumnOrderInput, setEditedColumnOrderInput] = useState(initialColumnOrder.join(', '));
    const [editedTableNumber, setEditedTableNumber] = useState(String(initialTableNumber));
    const [editedTableGroup, setEditedTableGroup] = useState(String(initialTableGroup));
    const [editedNotifyIf, setEditedNotifyIf] = useState(initialNotifyIf);
    const [sqlError, setSqlError] = useState('');
    const [sqlMessage, setSqlMessage] = useState('');
    const [isTested, setIsTested] = useState(false);
    const [testResults, setTestResults] = useState(null);
    const [isTesting, setIsTesting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [useSqlBuilder, setUseSqlBuilder] = useState(false);
    const [copiedSuccess, setCopiedSuccess] = useState(false);
    const hasDatabaseStructure = Array.isArray(databaseStructure) && databaseStructure.length > 0;

    const applySqlUpdate = (nextSql) => {
        setEditedSql(nextSql);
        setIsTested(false);
        setTestResults(null);
        if (sqlError) {
            setSqlError('');
        }
    };

    useEffect(() => {
        if (!show) {
            return;
        }

        const name = initialValues?.name || 'Query Name';
        const description = initialValues?.description || 'Query Description';
        const chatGptPrompt = initialValues?.cgptPrompt || '';
        const sql = (initialValues?.sqlQuery || '').trim();
        const columnOrder = Array.isArray(initialValues?.columnOrder) ? initialValues.columnOrder : [];
        const tableNumber = initialValues?.tableNumber ?? '';
    const tableGroup = initialValues?.tableGroup ?? '';

        setEditedName(name);
        setEditedDescription(description);
        setEditedChatGptPrompt(chatGptPrompt);
        setEditedSql(sql);
        setEditedColumnOrder(columnOrder);
        setEditedColumnOrderInput(columnOrder.join(', '));
        setEditedTableNumber(String(tableNumber));
    setEditedTableGroup(String(tableGroup));
        setEditedNotifyIf(initialNotifyIf);
        setSqlError('');
        setIsTested(false);
        setTestResults(null);
        setUseSqlBuilder(false);
        setSqlMessage('');
    }, [
        show,
        initialValues?.name,
        initialValues?.description,
        initialValues?.cgptPrompt,
        initialValues?.sqlQuery,
        initialValues?.columnOrder,
        initialValues?.tableNumber,
        initialNotifyIf,
    ]);

    const handleSqlChange = (event) => {
        applySqlUpdate(event.target.value);
    };

    const handlePromptChange = (event) => {
        let value = event.target.value;
        setEditedChatGptPrompt(value);
    } 

    const handleColumnOrderChange = (event) => {
        const rawValue = event.target.value;
        setEditedColumnOrderInput(rawValue);
        const values = rawValue
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean);
        setEditedColumnOrder(values);
    };

    const handleTest = async () => {
        if (!onTest) {
            return;
        }

        const { valid, message, normalizedSql } = verifySqlSyntax(editedSql);

        if (!valid) {
            setSqlError(message);
            setIsTested(false);
            setTestResults(null);
            return;
        }

        setIsTesting(true);
        setSqlError('');
        setSqlMessage('');

        try {
            const response = await onTest(normalizedSql);
            const data = response?.result?.data;

            if (!data || data.length === 0) {
                setTestResults([]);
                if (response.success) {
                    setSqlMessage('The test was successful but no records match the search, if expected you may proceed. If not please review and edit your query.')
                    setIsTested(true);
                    return;
                }
                setSqlError(`The query seems to have failed.\n ${JSON.stringify(response?.error, null, 2) || ''}`);
                setIsTested(false);
                return;
            }

            const sortedData = data.map((row) => {
                const sortedRow = {};
                editedColumnOrder.forEach((col) => {
                    if (col in row) {
                        sortedRow[col] = row[col];
                    }
                });
                Object.keys(row).forEach((col) => {
                    if (!(col in sortedRow)) {
                        sortedRow[col] = row[col];
                    }
                });
                return sortedRow;
            });

            setTestResults(sortedData);
            setIsTested(true);
        } catch (error) {
            setIsTested(false);
            setSqlError('Failed to test SQL query.');
        } finally {
            setIsTesting(false);
        }
    };

    const handleSave = async () => {
        const { valid, message, normalizedSql } = verifySqlSyntax(editedSql);

        if (!valid) {
            setSqlError(message);
            return;
        }

        const sanitizedName = editedName.trim() || 'Query Name';
        const sanitizedDescription = editedDescription.trim() || 'Description missing';
        const sanitizedChatGptPrompt = editedChatGptPrompt.trim() || 'Prompt missing';
        const sanitizedTableNumber = editedTableNumber.trim();
        const sanitizedTableGroup = editedTableGroup.trim();
        const sanitizedOrder = Array.from(
            new Set(
                editedColumnOrder
                    .filter(Boolean)
                    .map((col) => col.trim())
                    .filter(Boolean)
            )
        );
        const sanitizedNotifyIf = Array.isArray(editedNotifyIf)
            ? editedNotifyIf
                  .map((group) => {
                      if (!Array.isArray(group)) {
                          return [];
                      }

                      return group
                          .filter((condition) => condition && typeof condition === 'object')
                          .map((condition) => {
                              const { kind, operator, value, column } = condition;
                              const slackNotification =
                                  condition?.slackNotification === true ||
                                  condition?.slackNotification === 1;
                              const notificationChannel = normalizeNotificationChannel(
                                  condition?.notificationChannel
                              );
                              const createHubspotTicket = condition?.createHubspotTicket === true;
                              const heading = createHubspotTicket && typeof condition?.ticket?.heading === 'string'
                                  ? condition.ticket.heading.trim()
                                  : '';
                              const pipeline = createHubspotTicket && condition?.ticket?.pipeline?.idValue
                                  ? { label: condition.ticket.pipeline.label || '', idValue: condition.ticket.pipeline.idValue }
                                  : null;
                              const stage = createHubspotTicket && condition?.ticket?.stage?.idValue
                                  ? { label: condition.ticket.stage.label || '', idValue: condition.ticket.stage.idValue }
                                  : null;
                              const ticketLabel = createHubspotTicket && condition?.ticket?.label?.idValue
                                  ? { label: condition.ticket.label.label || '', idValue: condition.ticket.label.idValue }
                                  : null;
                              if (!kind || value === undefined || value === null || value === '') {
                                  return null;
                              }

                              if (kind === 'rowCount') {
                                  const numericValue = Number(value);
                                  if (!Number.isFinite(numericValue)) {
                                      return null;
                                  }
                                  return {
                                      kind,
                                      operator,
                                      value: numericValue,
                                      slackNotification,
                                      notificationChannel,
                                      createHubspotTicket,
                                      ticket: { heading, pipeline, stage, label: ticketLabel },
                                  };
                              }

                              if (kind === 'columnValue') {
                                  const normalizedColumn = typeof column === 'string' ? column.trim() : '';
                                  if (!normalizedColumn || !operator) {
                                      return null;
                                  }
                                  return {
                                      kind,
                                      operator,
                                      column: normalizedColumn,
                                      value,
                                      slackNotification,
                                      notificationChannel,
                                      createHubspotTicket,
                                      ticket: { heading, pipeline, stage, label: ticketLabel },
                                  };
                              }

                              if (kind === 'columnContains') {
                                  const normalizedColumn = typeof column === 'string' ? column.trim() : '';
                                  const stringValue = value === undefined || value === null ? '' : String(value);
                                  if (!normalizedColumn || stringValue.trim() === '') {
                                      return null;
                                  }
                                  return {
                                      kind: 'columnContains',
                                      column: normalizedColumn,
                                      value: stringValue,
                                      slackNotification,
                                      notificationChannel,
                                      createHubspotTicket,
                                      ticket: { heading, pipeline, stage, label: ticketLabel },
                                  };
                              }

                              if (kind === 'timeSince') {
                                  const numericValue = Number(value);
                                  const normalizedColumn = typeof column === 'string' ? column.trim() : '';
                                  if (!normalizedColumn || !Number.isFinite(numericValue)) {
                                      return null;
                                  }
                                  return {
                                      kind,
                                      column: normalizedColumn,
                                      value: numericValue,
                                      slackNotification,
                                      notificationChannel,
                                      createHubspotTicket,
                                      ticket: { heading, pipeline, stage, label: ticketLabel },
                                  };
                              }

                              return null;
                          })
                          .filter(Boolean);
                  })
                  .filter((group) => group.length > 0)
            : [];

        try {
            setIsSaving(true);
            await onSave({
                name: sanitizedName,
                description: sanitizedDescription,
                cgptPrompt: sanitizedChatGptPrompt,
                sqlQuery: normalizedSql,
                columnOrder: sanitizedOrder,
                tableNumber: sanitizedTableNumber,
                tableGroup: sanitizedTableGroup,
                notifyIf: sanitizedNotifyIf,
            });
        } catch (error) {
            setSqlError('Failed to save SQL query.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!onDelete) {
            return;
        }
        await onDelete();
    };

    const copyChatGptPromptToClipboard = () => {
        const fullCgptPrompt = `
Hi, 
I need a MySQL query to fetch some data from my database.
Please note that it must work for MySQL version 5.7 running on an Ubuntu server. 

Specifically:
${editedChatGptPrompt}

Lastly, here is my full database with all tables and their columns, including column names and data types:

${JSON.stringify(databaseStructure, null, 2)}

IMPORTANT LAST NOTES!
1. Try to avoid log tables as they are quite large and tend to slow down the queries. 
2. I want the result in a completely copyable state, meaning absolutely no comments or other text that will break a pure copy-paste of the query you give me.
`
        navigator.clipboard.writeText(fullCgptPrompt)
            .then(() => {
                setCopiedSuccess(true);
            }
            )
            .catch((err) => {
                alert("Error copying table structure to clipboard:");
                setCopiedSuccess(false);
            });

        
    }

    return (
        <Modal show={show} close={onClose}>
            <ModalContainer>
                {onDelete? <h2>Edit Table</h2>:<h2>Create Table</h2> }
                <FieldGroup>
                    <FieldLabel htmlFor={`table-name-${tableId}`}>Table name</FieldLabel>
                    <InputField
                        id={`table-name-${tableId}`}
                        value={editedName}
                        onChange={(event) => setEditedName(event.target.value)}
                    />
                </FieldGroup>
                <FieldGroup>
                    <FieldLabel htmlFor={`table-description-${tableId}`}>
                        Description
                    </FieldLabel>
                    <StyledTextarea
                        id={`table-description-${tableId}`}
                        rows={3}
                        value={editedDescription}
                        onChange={(event) => setEditedDescription(event.target.value)}
                    />
                </FieldGroup>
                <FieldGroup>
                    <FieldLabel htmlFor={`table-sql-${tableId}`}>SQL</FieldLabel>
                    {hasDatabaseStructure && (
                        <Button
                            tertiary
                            small
                            type="button"
                            onClick={() => setUseSqlBuilder(!useSqlBuilder)}
                            style={{ marginBottom: '8px' }}
                        >
                            {useSqlBuilder ? 'Hide Chat GPT Helper' : 'Create Chat GPT Prompt'}
                        </Button>
                    )}
                    {(useSqlBuilder && hasDatabaseStructure) && <>
                        <FieldLabel>What do you need?</FieldLabel>
                        <StyledTextarea
                            id={`table-prompt-${tableId}`}
                            rows={5}
                            value={editedChatGptPrompt}
                            onChange={handlePromptChange}
                        />
                        <Button primary small disabled={!editedChatGptPrompt} onClick={() => copyChatGptPromptToClipboard()}>Copy Chat GPT Prompt</Button>
                        {copiedSuccess && <span style={{color: theme.colors.greenEnergy}}>Table structure copied to clipboard!</span>}
                    </>}
                    <FieldLabel style={{marginTop: '12px'}}>Sql Query</FieldLabel>
                    <StyledTextarea
                        id={`table-sql-${tableId}`}
                        rows={8}
                        value={editedSql}
                        onChange={handleSqlChange}
                    />
                    {sqlMessage && <ModalText style={{ color: theme.colors.greenEnergy }}>{sqlMessage}</ModalText>}
                    {sqlError && <ErrorMessage>{sqlError}</ErrorMessage>}

                </FieldGroup>
                <ButtonRowContainer style={{ borderTop: `solid 2px ${theme.colors.grayBorder}`, paddingTop: '24px' }}>
                    <Button primary type="button" onClick={handleTest} disabled={isTesting}>
                        {isTesting ? 'Testing…' : 'Test'}
                    </Button>
                </ButtonRowContainer>

                {isTested && testResults && (
                    <>
                        <h3>Test Results</h3>
                        <FieldLabel>For tests only showing up to first 5 rows</FieldLabel>
                        <TestTableWrapper>
                            <TestTable>
                                <thead>
                                    <tr>
                                        {Object.keys(testResults[0] || {}).map((column) => (
                                            <TestTableHeadCell key={column}>
                                                {column}
                                            </TestTableHeadCell>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {testResults.map((row, rowIndex) => {
                                        if (rowIndex >= 5) {
                                            return null;
                                        }

                                        return (
                                            <TestTableRow key={rowIndex}>
                                                {Object.keys(testResults[0] || {}).map((column, columnIndex) => (
                                                    <TestTableCell
                                                        key={`${rowIndex}-${column}`}
                                                        isFirstColumn={columnIndex === 0}
                                                    >
                                                        {formatCellValue(row[column])}
                                                    </TestTableCell>
                                                ))}
                                            </TestTableRow>
                                        );
                                    })}
                                </tbody>
                            </TestTable>
                        </TestTableWrapper>
                    </>
                )}

                <SectionHeading>Add column and table order</SectionHeading>
                <FieldGroup>
                    <FieldLabel htmlFor={`table-columns-${tableId}`}>
                        Column order (comma separated)
                    </FieldLabel>
                    <InputField
                        id={`table-columns-${tableId}`}
                        value={editedColumnOrderInput}
                        onChange={handleColumnOrderChange}
                        placeholder="columnOne, columnTwo"
                    />
                </FieldGroup>
                <FieldGroup>
                    <FieldLabel htmlFor={`table-number-${tableId}`}>
                        Table number
                    </FieldLabel>
                    <InputField
                        id={`table-number-${tableId}`}
                        type="number"
                        value={editedTableNumber}
                        onChange={(event) => {
                            setEditedTableNumber(event.target.value);
                        }}
                        placeholder="e.g. 42"
                    />
                </FieldGroup>
                <FieldGroup>
                    <FieldLabel htmlFor={`table-group-${tableId}`}>
                        Table group (optional)
                    </FieldLabel>
                    <InputField
                        id={`table-group-${tableId}`}
                        value={editedTableGroup}
                        onChange={(event) => {
                            setEditedTableGroup(event.target.value);
                        }}
                        placeholder="e.g. Customer Success"
                    />
                </FieldGroup>


                <CustomSqlNotificationEditor
                    value={editedNotifyIf}
                    onChange={setEditedNotifyIf}
                    columnSuggestions={editedColumnOrder}
                />
                
                {!isTested &&<>
                    
                        <FieldGroup style={{borderTop: `solid 2px ${theme.colors.grayBorder}`, paddingTop: '24px', marginTop: '20px'}}>You must test the query before saving.</FieldGroup>
                        <Button primary small type="button" onClick={handleTest} disabled={isTesting}>
                            {isTesting ? 'Testing…' : 'Test'}
                        </Button>
                </>}
                <ButtonRowContainer style={{ borderTop: `solid 2px ${theme.colors.grayBorder}`, paddingTop: '24px' }}>
                    <Button
                        primary
                        disabled={!isTested || isSaving}
                        type="button"
                        onClick={handleSave}
                    >
                        {isSaving ? 'Saving…' : 'Save Sql Query'}
                    </Button>
                    <Button secondary type="button" onClick={onClose}>
                        Cancel
                    </Button>
                </ButtonRowContainer>
                {onDelete && (
                    <ButtonRowContainer style={{ borderTop: `solid 2px ${theme.colors.grayBorder}`, paddingTop: '24px' }}>
                        <Button danger type="button" onClick={handleDelete}>
                            Delete Table
                        </Button>
                    </ButtonRowContainer>
                )}
            </ModalContainer>
        </Modal>
    );
};

export default CustomSqlTableModal;
